import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MutableRefObject } from 'react';
import { Board, FocusedPlace, PlaceId, ApiObjectiveSlapResponse, StickerDesignId, SlapId } from '../utils/Primitives';
import { getPlace } from '../places/Places';
import { lookupExpandedSlap } from '../slaps/Slaps';
import AddressDisplay from '../components/AddressDisplay';
import { getPrimaryExplorerUrlForAddress } from "@/app/utils/GeneralUtilities";
import { useChainId } from 'wagmi';
import { formatDateTime } from '../utils/GeneralUtilities';
import SlapRowShallow from '../sidebar/SlapRowShallow';

type LatestSlapsProps = {
    boardRef: MutableRefObject<Board>;
    fetchUrl: string;
    slapsPerPage: number;
    setFocusOnPlace: (place: FocusedPlace) => void;
    handleStickerClick: (stickerId: StickerDesignId, e: React.MouseEvent<HTMLDivElement>) => void;
    handleSlapClick: (slapId: SlapId, e: React.MouseEvent<HTMLDivElement>) => void;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    initialData?: any[];
}

export default function LatestSlaps({
    boardRef,
    fetchUrl,
    slapsPerPage,
    setFocusOnPlace,
    handleStickerClick,
    handleSlapClick,
    currentPage,
    setCurrentPage,
    initialData
}: LatestSlapsProps) {
    const chainId = useChainId();

    const { data: objectiveSlaps, isLoading } = useQuery({
        queryKey: ['latestSlaps', fetchUrl],
        queryFn: async () => {
            console.log('LatestSlaps fetching data from:', fetchUrl);
            const response = await fetch(fetchUrl);
            if (!response.ok) {
                throw new Error('Failed to fetch slaps');
            }
            const data = await response.json();
            console.log('LatestSlaps fetched data:', data);
            return data.data || [];
        },
        initialData,
        staleTime: 30000
    });

    // Calculate pagination based on fetched data
    const totalPages = Math.max(1, Math.ceil((objectiveSlaps?.length || 0) / slapsPerPage));
    const visibleObjectiveSlaps = objectiveSlaps?.slice(
        (currentPage - 1) * slapsPerPage,
        currentPage * slapsPerPage
    ) || [];

    const handleNextPageClick = () => {
        console.log('Clicking next page, current page:', currentPage, 'total pages:', totalPages);
        setCurrentPage(Math.min(currentPage + 1, totalPages));
    };

    const handlePreviousPageClick = () => {
        console.log('Clicking previous page, current page:', currentPage, 'total pages:', totalPages);
        setCurrentPage(Math.max(currentPage - 1, 1));
    };

    const handlePlayerClick = (e: React.MouseEvent, playerAddress: string) => {
        e.preventDefault();
        e.stopPropagation();
        const url = getPrimaryExplorerUrlForAddress(chainId, playerAddress);
        if (url) {
            window.open(url, '_blank');
        }
    };

    return (
        <div className="mt-4 pt-2 bg-white rounded-lg shadow p-3 w-full">
            <div className="relative flex items-center justify-center h-8 mb-2">
                {totalPages > 1 && (
                    <>
                        <div className="absolute left-0 top-1/2 -translate-y-1/2">
                            {currentPage > 1 ? (
                                <button
                                    onClick={handlePreviousPageClick}
                                    className="text-lg px-3 py-0.5 rounded bg-slate-100 hover:bg-slate-200 select-none"
                                >
                                    ‹
                                </button>
                            ) : <span className="invisible text-lg select-none">‹</span>}
                        </div>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2">
                            {currentPage < totalPages ? (
                                <button
                                    onClick={handleNextPageClick}
                                    className="text-lg px-3 py-0.5 rounded bg-slate-100 hover:bg-slate-200 select-none"
                                >
                                    ›
                                </button>
                            ) : <span className="invisible text-lg select-none">›</span>}
                        </div>
                    </>
                )}
                <h3 className="text-xs font-medium select-none">Recent Slaps</h3>
            </div>

            <div className="space-y-2">
                {isLoading ? (
                    <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    </div>
                ) : visibleObjectiveSlaps.length > 0 ? (
                    visibleObjectiveSlaps.map((objectiveSlap: ApiObjectiveSlapResponse) => (
                        <SlapRowShallow
                            boardRef={boardRef}
                            key={objectiveSlap.slapId}
                            objectiveSlap={objectiveSlap}
                            handleSlapClick={handleSlapClick}
                            handleStickerClick={handleStickerClick}
                            setFocusOnPlace={setFocusOnPlace}
                        />
                    ))
                ) : (
                    <div className="flex justify-center items-center text-xs text-slate-500">None</div>
                )}
            </div>
        </div>
    );
}
